import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Box, FormProvider, Preloader } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import { WarnModal } from '@/components/elements';
import { ContactStatuses } from '@/modules/contact/types/Contact.types';
import { useHistory } from '@/modules/core/hooks';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import { Footer, Header, Sidebar } from '@/modules/ExpressSetup/ExpressSetupSignUp/components';
import { ExpressSetupSignUpErrorText, ExpressSetupSignUpServerErrorText } from '@/modules/ExpressSetup/ExpressSetupSignUp/expressSetupSignUp.constants';
import useCreateOrganizationForm, { CreateOrganizationField, CreateOrganizationForm } from '@/modules/ExpressSetup/ExpressSetupSignUp/hooks/useCreateOrganizationForm';
import useCreateExpressSetupOrganizationQuery from '@/modules/ExpressSetup/ExpressSetupSignUp/queries/useCreateExpressSetupOrganization.query';
import { useAuthStore } from '@/modules/user/stores';

import BasicProperties from './BasicProperties/BasicProperties';

const CreateOrganization = () => {
  const history = useHistory();
  const { auth, user } = useAuthStore();
  const { observingOrganizationData } = useOrganizationOverridableProperties();
  const { methods } = useCreateOrganizationForm();
  const { handleCreate, isLoading } = useCreateExpressSetupOrganizationQuery();
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const handleSuccess = useCallback((organizationId?: string) => {
    if (organizationId) {
      history.push(ROUTES.EXPRESS_SETUP_BY_ID(organizationId));
    } else {
      history.push(ROUTES.HOME);
    }
    window.location.reload();
  }, [history]);

  const handleSubmit = useCallback(async (formValues: CreateOrganizationForm) => {
    try {
      const createdOrganization = await handleCreate({
        partner: user?.organizationId,
        contactId: user?.contactId,
        legalName: formValues[CreateOrganizationField.legalName],
        displayName: formValues[CreateOrganizationField.displayName],
        employerIdType: formValues[CreateOrganizationField.employerIdType],
        employerId: formValues[CreateOrganizationField.employerId],
        taxId: formValues[CreateOrganizationField.taxId],
        addressLine1: formValues[CreateOrganizationField.addressLine1],
        addressLine2: formValues[CreateOrganizationField.addressLine2],
        city: formValues[CreateOrganizationField.city],
        state: formValues[CreateOrganizationField.state],
        zipCode: formValues[CreateOrganizationField.zipCode],
      });
      setIsErrorModalVisible(false);
      handleSuccess(createdOrganization?.org_id);
    } catch (error) {
      const message = _.get(error, 'response.data.elevate_error_message', '') as string;
    
      if (message.includes(ExpressSetupSignUpServerErrorText.ORGANIZATION_ALREADY_EXIST)) {
        methods.setError(CreateOrganizationField.employerId, {
          type: 'manual',
          message: ExpressSetupSignUpErrorText.ORGANIZATION_ALREADY_EXIST,
        });
        setIsErrorModalVisible(false);
        return;
      }

      setIsErrorModalVisible(true);
    }
  }, [handleCreate, user, methods, handleSuccess]);

  useLayoutEffect(() => {
    if (auth && user?.contactStatusType && user?.contactStatusType !== ContactStatuses.EXPRESS_ORGANIZATION) {
      history.push(ROUTES.HOME);
      return;
    }
  }, [auth, user, history]);

  if (!user) {
    return <Box margin="auto"><Preloader testId="create-organization_preloader" /></Box>;
  }

  return (
    <>
      <Box data-testId="create-organization_wrapper" direction="row">
        <Sidebar
          testId="create-organization"
          largeLogoUrl={observingOrganizationData?.logoInfo?.largeLogoUrl}
          darkLargeLogoUrl={observingOrganizationData?.logoInfo?.darkLargeLogoUrl}
        />

        <Box flex>
          <Box pad="spacing48">
            <Header
              title="Almost There! Let’s Set up Your Organization"
              description="To get your organization up and running, fill in some basic properties."
            />
            <FormProvider {...methods}>
              <BasicProperties />
            </FormProvider>
          </Box>

          <Footer
            testId="create-organization"
            onSubmit={methods.handleSubmit(handleSubmit)}
            isLoading={isLoading}
          />
        </Box>
      </Box>
      {isErrorModalVisible && (
        <WarnModal
          testId="create-organization_error"
          visible={isErrorModalVisible}
          onSetVisible={setIsErrorModalVisible}
          header="The server encountered an error processing the request"
          helptext={`It doesn't appear to have affected your data, but we cannot save the Organization with the latest records.
          Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
          buttonText="Close"
          submitButtonText="Try Again"
          onSubmit={methods.handleSubmit(handleSubmit)}
          isAwaitMode
          disableAutomaticClosing
        />
      )}
    </>
  );
};

export default CreateOrganization;
